"use client";

import { useState, useEffect, ReactNode } from "react";
import { useSearchParams } from "next/navigation";
import Header from '@/components/layout/header';
import Footer from '@/components/layout/footer';
import MobileHeader from '@/components/layout/mobile-header';
import MobileBottomNav from '@/components/layout/mobile-bottom-nav';
// import { Toaster } from "@/components/ui/toaster";
import { LoginModal } from "@/components/auth/login";
import { useAuth } from "@/hooks/use-auth";
import { 
  HeroShimmer, 
  CategoriesShimmer, 
  FeaturedRestaurantsShimmer, 
  Shimmer 
} from "@/components/ui/shimmer";
import dynamic from 'next/dynamic';
import { useMarketplaceStore } from "@/store/marketplace-store";
import { useMarketplace } from '@/hooks/useMarketplace';

// Dynamically import the MarketplaceThemeProvider with no SSR
const MarketplaceThemeProvider = dynamic(
  () => import('@/components/marketplace/theme-provider').then(mod => mod.MarketplaceThemeProvider),
  { ssr: false }
);

/**
 * ClientLayout
 * 
 * Client component that handles the shimmer loading effect
 * and renders the actual layout once loading is complete.
 * Coordinates loading state with marketplace store initialization.
 */
export function ClientLayout({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const marketplaceStore = useMarketplaceStore();
  const searchParams = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { tcpParams } = useMarketplace();
  // Effect to handle auth parameter and show login modal
  useEffect(() => {
    const authRequired = searchParams?.get('auth') === 'required';
    // Only show login modal if auth is required AND user is not authenticated
    if (authRequired && !isAuthenticated) {
      setShowLoginModal(true);
    } else if (isAuthenticated) {
      // If user is authenticated, ensure modal is closed
      setShowLoginModal(false);
    }
    
    // Remove the auth parameter from the URL without a page reload
    if (authRequired) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams, isAuthenticated]);

  useEffect(() => {
    // This effect manages the loading state based on marketplace store status
    
    // Maximum time to show loading state before forcing render (prevents infinite loading)
    const MAX_LOADING_TIME = 3000; // 3 seconds
    
    // If we already have the config data, we can render immediately
    if (marketplaceStore.config !== null) {
      console.log('[ClientLayout] Marketplace config available, showing content');
      setIsLoading(false);
      return;
    }
    
    // Otherwise, show shimmer until config is loaded
    setIsLoading(true);
    
    
    // If the store is not initialized yet and not currently loading, trigger initialization
    if (!marketplaceStore.isInitialized && !marketplaceStore.isLoading) {
      console.log('[ClientLayout] Triggering marketplace initialization');
      marketplaceStore.initialize().catch((error: Error) => {
        console.error('[ClientLayout] Initialization error:', error);
        // End loading state even on error after a short delay
        setTimeout(() => setIsLoading(false), 1000);
      });
    }
    
    // Set up a listener for store changes
    const checkInterval = setInterval(() => {
      if (marketplaceStore.config) {
        console.log('[ClientLayout] Config received, ending loading state');
        setIsLoading(false);
        clearInterval(checkInterval);
      } else if (marketplaceStore.error) {
        console.error('[ClientLayout] Error loading marketplace config:', marketplaceStore.error);
        // End loading state on error after a short delay
        setTimeout(() => {
          console.log('[ClientLayout] Ending loading state despite error');
          setIsLoading(false);
        }, 1000);
        clearInterval(checkInterval);
      }
    }, 500);
    
    // Safety timeout - never show loading state for more than MAX_LOADING_TIME
    const safetyTimeout = setTimeout(() => {
      console.log('[ClientLayout] Safety timeout reached, forcing render');
      setIsLoading(false);
      clearInterval(checkInterval);
    }, MAX_LOADING_TIME);
    
    // Cleanup interval and timeout on unmount
    return () => {
      clearInterval(checkInterval);
      clearTimeout(safetyTimeout);
    };
  }, [marketplaceStore]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        {/* Header shimmer */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow">
          <div className="container mx-auto px-4 h-20 md:h-24 flex items-center justify-between">
            <Shimmer className="h-10 w-32" />
            <div className="hidden md:flex space-x-6">
              {[1, 2, 3, 4].map((i) => (
                <Shimmer key={i} className="h-4 w-20" />
              ))}
            </div>
            <div className="flex space-x-3">
              <Shimmer className="h-10 w-10 rounded-full" />
              <Shimmer className="h-10 w-10 rounded-full" />
            </div>
          </div>
        </header>
        
        {/* Main content shimmer */}
        <main className="flex-grow pt-20 md:pt-24">
          <HeroShimmer />
          <CategoriesShimmer />
          <FeaturedRestaurantsShimmer />
        </main>
        
        {/* Footer shimmer */}
        <footer className="bg-gray-900 py-12">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((col) => (
                <div key={col} className="space-y-4">
                  <Shimmer className="h-6 w-32 bg-gray-700" />
                  {[1, 2, 3, 4].map((i) => (
                    <Shimmer key={i} className="h-4 w-24 bg-gray-700" />
                  ))}
                </div>
              ))}
            </div>
            <div className="mt-10 pt-6 border-t border-gray-800">
              <Shimmer className="h-4 w-48 mx-auto bg-gray-700" />
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <>
      <div className="relative flex flex-col min-h-screen">
        <MarketplaceThemeProvider />
        {/* Desktop Header - hidden on mobile */}
        <div className="hidden md:block">
          <Header />
        </div>
        
        {/* Mobile Header - only visible on mobile */}
        <MobileHeader />
        
        <main className="flex-grow pt-0 md:pt-[5rem] pb-0 md:pb-0">
            {children}
        </main>
        
        {/* Conditionally render Footer */}
        {tcpParams?.utmSource !== 'tcp' && <Footer />}
        
        {/* Mobile Bottom Navigation - only visible on mobile */}
        <MobileBottomNav />
      </div>
      {/* Place Toaster outside the main layout to avoid z-index issues */}
      {/* <Toaster /> */}
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </>
  );
}
